import { defineStore } from "pinia"
import { computed, ref } from "vue"
import * as consumerAuthApi from "@/api/consumer/auth"
import type { User } from "@/api/shared/types"

/** Hasil login/register portal (tipe diekspor agar vue-tsc tidak mengeluh TS4025). */
export interface AuthActionResult {
  ok: boolean
  code?: string
  message?: string
}

/** Sesi pengguna konsumen (JWT terpisah dari panel admin). */
export const useConsumerAuthStore = defineStore("consumerAuth", () => {
  const token = ref<string | null>(localStorage.getItem("portal_token"))
  const user = ref<User | null>(null)

  const isAuthenticated = computed(() => !!token.value)

  function persistToken(t: string | null) {
    token.value = t
    if (t) localStorage.setItem("portal_token", t)
    else localStorage.removeItem("portal_token")
  }

  function logoutLocal() {
    user.value = null
    persistToken(null)
  }

  function syncUser(nextUser: User | null) {
    user.value = nextUser
  }

  async function login(email: string, password: string): Promise<AuthActionResult> {
    try {
      const res = await consumerAuthApi.portalLogin({ email, password })
      persistToken(res.token)
      user.value = res.user
      return { ok: true }
    } catch (error) {
      const e = error as { code?: string; message?: string }
      return { ok: false, code: e.code, message: e.message }
    }
  }

  async function loginWithGoogleIdToken(idToken: string): Promise<boolean> {
    try {
      const res = await consumerAuthApi.portalGoogleLogin(idToken)
      persistToken(res.token)
      user.value = res.user
      return true
    } catch (error) {
      const e = error as { response?: { data?: { message?: string } }; message?: string }
      const msg = e.response?.data?.message || e.message || "Gagal masuk dengan Google"
      console.warn("[portal] google login gagal:", msg)
      return false
    }
  }

  async function register(
    username: string,
    email: string,
    password: string,
  ): Promise<AuthActionResult> {
    try {
      await consumerAuthApi.portalRegister({ username, email, password })
      return { ok: true }
    } catch (error) {
      const e = error as { code?: string; message?: string }
      return { ok: false, code: e.code, message: e.message }
    }
  }

  async function logout(): Promise<void> {
    try {
      await consumerAuthApi.portalLogout()
    } catch {
      /* abaikan */
    }
    logoutLocal()
  }

  /** Satu permintaan profil portal per sesi paralel */
  let portalCheckPromise: Promise<boolean> | null = null

  async function checkAuth(): Promise<boolean> {
    if (!token.value) return false
    if (user.value) return true
    if (portalCheckPromise) return portalCheckPromise

    portalCheckPromise = (async () => {
      try {
        const profile = await consumerAuthApi.portalGetProfile()
        user.value = profile
        return true
      } catch {
        logoutLocal()
        return false
      } finally {
        portalCheckPromise = null
      }
    })()

    return portalCheckPromise
  }

  return {
    token,
    user,
    isAuthenticated,
    login,
    loginWithGoogleIdToken,
    register,
    logout,
    checkAuth,
    logoutLocal,
    syncUser,
  }
})
